import React, { useEffect, useMemo, useState } from 'react';
import {
  getRateCards, createRateCard, updateRateCard, deleteRateCard,
  testRateResolve, getBankHolidays, setBankHolidays,
  getInventoryParts, getUsers, seedStandardRates,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Plus, Trash2, FlaskConical, CalendarDays, AlertCircle, Sparkles, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { isoDateTimeLocal } from '../../lib/dateUtils';

/**
 * Rate Cards admin page.
 *
 * Provides a rules grid where the admin can set how an engineer's hourly
 * rate is resolved at billing time based on three dimensions:
 *   1) Employee  (or "Any")
 *   2) Customer  (or "Any")
 *   3) Time band (or "Any") — Mon-Fri day/eve/night, Sat, Sun, Bank holiday
 *
 * Plus a dry-run "tester" so the admin can sanity-check what rate engineer X
 * would get for customer Y at a given datetime — and an editable list of
 * UK bank holidays which the matcher uses to pick the bank-holiday band.
 *
 * Engineers never see this page nor the rates they're charged at.
 */
const TIME_BANDS = [
  { value: '', label: 'Any time' },
  { value: 'weekday_day', label: 'Weekday · 06:00–18:00' },
  { value: 'weekday_night', label: 'Weekday · 18:00–06:00' },
  { value: 'saturday_day', label: 'Saturday · 06:00–18:00' },
  { value: 'saturday_night', label: 'Saturday · 18:00–06:00' },
  { value: 'sunday_day', label: 'Sunday · 06:00–18:00' },
  { value: 'sunday_night', label: 'Sunday · 18:00–06:00' },
  { value: 'bank_holiday_day', label: 'Bank holiday · 06:00–18:00' },
  { value: 'bank_holiday_night', label: 'Bank holiday · 18:00–06:00' },
];

const RateCards = () => {
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const [r, u, p] = await Promise.all([
      getRateCards(),
      getUsers(),
      getInventoryParts(),
    ]);
    setRules(r);
    setUsers(u);
    setRates(p.filter(x => !x.isDraft && (x.category === 'Labor' || x.xeroIsTracked === false)));
  };

  useEffect(() => {
    refresh().catch(() => toast.error('Failed to load rate cards')).finally(() => setLoading(false));
  }, []);

  const employees = useMemo(() => users.filter(u => u.role === 'employee'), [users]);
  const customers = useMemo(() => users.filter(u => u.role === 'customer'), [users]);
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const rateById = useMemo(() => Object.fromEntries(rates.map(r => [r.id, r])), [rates]);

  if (loading) return <div className="p-8 text-slate-500">Loading rate cards…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-8 max-w-6xl">
      <PageHeader
        title="Rate Cards"
        description="Hourly rates by engineer × customer × time of day. Add ONE rule per time band you want to charge differently — each rule can carry its own optional call-out flat fee, so weekends / evenings / holidays can bill higher than weekday-day. Engineers never see rates."
      />

      <RulesTable
        rules={rules}
        rates={rates}
        employees={employees}
        customers={customers}
        userById={userById}
        rateById={rateById}
        onRefresh={refresh}
      />

      <ResolverTester employees={employees} customers={customers} rateById={rateById} />

      <BankHolidayEditor />
    </div>
  );
};

// ───────────────────── Rules table ─────────────────────
const RulesTable = ({ rules, rates, employees, customers, userById, rateById, onRefresh }) => {
  const [adding, setAdding] = useState(false);
  // When non-null, the form above is in edit-mode for that rule id — it's
  // the same form as Add, wired to PATCH instead of POST.
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ employeeId: '', customerId: '', timeBand: '', rateAmount: '', priority: 0, callOutFlatFee: '', callOutIncludedHours: '1' });

  const resetDraft = () => setDraft({ employeeId: '', customerId: '', timeBand: '', rateAmount: '', priority: 0, callOutFlatFee: '', callOutIncludedHours: '1' });

  const openEdit = (rule) => {
    setAdding(false);
    setEditingId(rule.id);
    setDraft({
      employeeId: rule.employeeId || '',
      customerId: rule.customerId || '',
      timeBand: rule.timeBand || '',
      // Edit always uses the direct rateAmount field. Legacy rules with a
      // rateId get their effective £/h dropped in so the admin can nudge it
      // without being blocked by the old parts_v2 lookup.
      rateAmount: rule.rateAmount != null
        ? String(rule.rateAmount)
        : (rule.rateId && rateById[rule.rateId]
            ? String(rateById[rule.rateId].sellPrice ?? rateById[rule.rateId].unitCost ?? 0)
            : ''),
      priority: rule.priority ?? 0,
      callOutFlatFee: rule.callOutFlatFee != null ? String(rule.callOutFlatFee) : '',
      callOutIncludedHours: rule.callOutIncludedHours != null ? String(rule.callOutIncludedHours) : '1',
    });
    // The form renders at the top of this section. On mobile, clicking a
    // pencil at the bottom of the list would otherwise look like nothing
    // happens because the form is off-screen.
    setTimeout(() => {
      document.querySelector('[data-testid="rate-cards-add-form"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const cancelForm = () => {
    setAdding(false);
    setEditingId(null);
    resetDraft();
  };

  const submit = async () => {
    const amt = parseFloat(draft.rateAmount);
    if (!Number.isFinite(amt) || amt < 0) return toast.error('Enter a rate (£/hour)');
    // Call-out fields are optional — treat blank as "no call-out contract on
    // this rule". If the admin sets a flat fee, the "included hours" defaults
    // to 1 h so the fee absorbs a normal on-site visit.
    const feeRaw = String(draft.callOutFlatFee || '').trim();
    const feeNum = feeRaw === '' ? null : parseFloat(feeRaw);
    if (feeNum !== null && (!Number.isFinite(feeNum) || feeNum < 0)) {
      return toast.error('Call-out fee must be a non-negative number');
    }
    const includedRaw = String(draft.callOutIncludedHours || '').trim();
    const includedNum = includedRaw === '' ? 1.0 : parseFloat(includedRaw);
    if (feeNum !== null && (!Number.isFinite(includedNum) || includedNum < 0)) {
      return toast.error('Included hours must be a non-negative number');
    }
    const payload = {
      employeeId: draft.employeeId || null,
      customerId: draft.customerId || null,
      timeBand: draft.timeBand || null,
      rateAmount: amt,
      priority: Number(draft.priority) || 0,
      callOutFlatFee: feeNum,
      callOutIncludedHours: feeNum !== null ? includedNum : null,
    };
    try {
      if (editingId) {
        // PATCH clears any legacy rateId so the edited rule uses rateAmount
        // as the single source of truth going forward.
        await updateRateCard(editingId, { ...payload, rateId: null });
        toast.success('Rule updated');
      } else {
        await createRateCard(payload);
        toast.success('Rule added');
      }
      cancelForm();
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editingId ? 'Update failed' : 'Add failed'));
    }
  };

  const remove = async (rule) => {
    if (!window.confirm('Delete this rate-card rule?')) return;
    try {
      await deleteRateCard(rule.id);
      await onRefresh();
      toast.success('Rule deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const labelFor = (id, list, fallback = 'Any') => {
    if (!id) return <span className="text-slate-400 italic">{fallback}</span>;
    const u = userById[id];
    return u ? (u.company || u.name) : id;
  };
  const rateLabel = (rule) => {
    // New path: admin typed a rate directly.
    let base;
    if (rule.rateAmount != null) {
      const nm = (rule.rateName || '').trim();
      base = nm ? `${nm} · £${Number(rule.rateAmount).toFixed(2)}/h` : `£${Number(rule.rateAmount).toFixed(2)}/h`;
    } else {
      // Legacy path: rateId → Labour part.
      const r = rule.rateId ? rateById[rule.rateId] : null;
      base = r ? `${r.name} · £${(r.sellPrice ?? r.unitCost ?? 0).toFixed(2)}/h` : (rule.rateId || '—');
    }
    if (rule.callOutFlatFee != null) {
      const inc = rule.callOutIncludedHours != null ? Number(rule.callOutIncludedHours) : 1;
      return `${base} · Call-out £${Number(rule.callOutFlatFee).toFixed(2)} (first ${inc}h)`;
    }
    return base;
  };
  const bandLabel = (b) => (TIME_BANDS.find(t => t.value === (b || '')) || TIME_BANDS[0]).label;
  const [seedOpen, setSeedOpen] = useState(false);
  const formOpen = adding || editingId != null;

  return (
    <section data-testid="rate-cards-rules">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-slate-900">Rules</h2>
        {!formOpen && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSeedOpen(true)}
              data-testid="rate-cards-seed-btn"
              title="Create 8 rules at UK standard multipliers from a base £/h"
            >
              <Sparkles className="h-4 w-4 mr-1.5" /> Seed UK standard rates
            </Button>
            <Button size="sm" onClick={() => { setAdding(true); setEditingId(null); resetDraft(); }} data-testid="rate-cards-add-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add rule
            </Button>
          </div>
        )}
      </div>

      <SeedStandardDialog
        open={seedOpen}
        onClose={() => setSeedOpen(false)}
        employees={employees}
        customers={customers}
        onSeeded={onRefresh}
      />

      {formOpen && (
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-3 space-y-3" data-testid="rate-cards-add-form">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            {editingId ? `Editing rule ${editingId}` : 'New rule'}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <PickerField label="Engineer" value={draft.employeeId} onChange={v => setDraft({ ...draft, employeeId: v })} options={[{ id: '', label: 'Any engineer' }, ...employees.map(e => ({ id: e.id, label: e.name }))]} testId="rate-cards-employee" />
            <PickerField label="Customer" value={draft.customerId} onChange={v => setDraft({ ...draft, customerId: v })} options={[{ id: '', label: 'Any customer' }, ...customers.map(c => ({ id: c.id, label: c.company || c.name }))]} testId="rate-cards-customer" />
            <PickerField label="Time band" value={draft.timeBand} onChange={v => setDraft({ ...draft, timeBand: v })} options={TIME_BANDS.map(t => ({ id: t.value, label: t.label }))} testId="rate-cards-band" />
            <div>
              <Label className="text-xs">Rate (£/hour)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="e.g. 65.00"
                value={draft.rateAmount}
                onChange={e => setDraft({ ...draft, rateAmount: e.target.value })}
                className="mt-1"
                data-testid="rate-cards-rate"
              />
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Input
                type="number"
                min="0"
                max="999"
                value={draft.priority}
                onChange={e => setDraft({ ...draft, priority: e.target.value })}
                className="mt-1"
                data-testid="rate-cards-priority"
              />
            </div>
          </div>

          {/* Call-out contract — optional. When set, a customer-lane
              clock-in flagged as `callOut=true` bills as one flat-fee
              line covering the included hours, plus normal hourly for
              anything over. Leave blank to disable call-out billing on
              this rule. Because the flat fee lives on the rule (which
              is band-specific), admins can charge different call-out
              fees for weekday-day, weekday-evening, weekend, etc — one
              rule per band. */}
          <div className="border-t border-slate-100 pt-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1.5">
              <AlertCircle className="h-3 w-3 text-amber-600" />
              Call-out contract (optional, this band only)
            </div>
            <div className="text-[11px] text-slate-500 mb-2 leading-snug">
              Applies when the engineer ticks &ldquo;This is a call-out&rdquo; at clock-in
              <strong> and</strong> the visit falls in the{' '}
              <span className="text-slate-800 font-medium">
                {draft.timeBand
                  ? (TIME_BANDS.find(t => t.value === draft.timeBand)?.label || draft.timeBand)
                  : 'Any time band'}
              </span>{' '}
              window. Want different call-out fees for weekends or evenings? Create separate rules for those bands.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Flat fee (£)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="e.g. 250.00"
                  value={draft.callOutFlatFee}
                  onChange={e => setDraft({ ...draft, callOutFlatFee: e.target.value })}
                  className="mt-1"
                  data-testid="rate-cards-callout-fee"
                />
              </div>
              <div>
                <Label className="text-xs">Includes first (hours)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.25"
                  inputMode="decimal"
                  placeholder="1"
                  value={draft.callOutIncludedHours}
                  onChange={e => setDraft({ ...draft, callOutIncludedHours: e.target.value })}
                  className="mt-1"
                  disabled={!String(draft.callOutFlatFee || '').trim()}
                  data-testid="rate-cards-callout-included"
                />
              </div>
              <div className="text-[11px] text-slate-500 self-end pb-1.5">
                {String(draft.callOutFlatFee || '').trim()
                  ? `£${draft.callOutFlatFee} covers first ${draft.callOutIncludedHours || '1'}h; then £${draft.rateAmount || '?'}/h.`
                  : 'Blank = engineer\'s callout flag on this rule falls back to the normal hourly rate.'}
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={cancelForm}>Cancel</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={submit} data-testid="rate-cards-submit">
              {editingId ? 'Save changes' : 'Save rule'}
            </Button>
          </div>
        </div>
      )}

      {/* Rules list — table on ≥sm viewports, stacked cards on mobile so
          the "£52.50 · Weekday night" labels never truncate. */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {/* ≥sm: full table */}
        <table className="hidden sm:table w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium text-slate-600 text-xs uppercase tracking-wider">Engineer</th>
              <th className="px-4 py-2.5 font-medium text-slate-600 text-xs uppercase tracking-wider">Customer</th>
              <th className="px-4 py-2.5 font-medium text-slate-600 text-xs uppercase tracking-wider">When</th>
              <th className="px-4 py-2.5 font-medium text-slate-600 text-xs uppercase tracking-wider">Rate</th>
              <th className="px-4 py-2.5 font-medium text-slate-600 text-xs uppercase tracking-wider">Priority</th>
              <th className="px-4 py-2.5 font-medium text-slate-600 text-xs uppercase tracking-wider text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rules.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                No rules yet. Add one above to start charging your engineers' time correctly.
              </td></tr>
            )}
            {rules.map(r => (
              <tr key={r.id} data-testid={`rate-cards-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3">{labelFor(r.employeeId, employees)}</td>
                <td className="px-4 py-3">{labelFor(r.customerId, customers)}</td>
                <td className="px-4 py-3 text-slate-700">{bandLabel(r.timeBand)}</td>
                <td className="px-4 py-3 text-slate-900 font-medium">{rateLabel(r)}</td>
                <td className="px-4 py-3 text-slate-600 tabular-nums">{r.priority}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-0.5">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => openEdit(r)}
                      data-testid={`rate-cards-edit-${r.id}`}
                      title="Edit rule"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(r)} data-testid={`rate-cards-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* <sm: stacked cards */}
        <ul className="sm:hidden divide-y divide-slate-100">
          {rules.length === 0 && (
            <li className="px-4 py-12 text-center text-slate-500 text-sm">
              No rules yet. Add one above to start charging your engineers' time correctly.
            </li>
          )}
          {rules.map(r => (
            <li
              key={r.id}
              data-testid={`rate-cards-row-mobile-${r.id}`}
              className="px-4 py-3 flex items-start gap-3"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="text-sm font-semibold text-slate-900 break-words">{rateLabel(r)}</div>
                <div className="text-xs text-slate-600 break-words">{bandLabel(r.timeBand)}</div>
                <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2 gap-y-0.5">
                  <span className="inline-flex items-center gap-1">
                    <span className="text-slate-400">Engineer:</span>
                    <span className="text-slate-700">{labelFor(r.employeeId, employees)}</span>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="text-slate-400">Customer:</span>
                    <span className="text-slate-700">{labelFor(r.customerId, customers)}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <span className="text-slate-400">Priority:</span>
                    <span className="text-slate-700">{r.priority}</span>
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => openEdit(r)}
                  data-testid={`rate-cards-edit-mobile-${r.id}`}
                  title="Edit rule"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => remove(r)}
                  data-testid={`rate-cards-delete-mobile-${r.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5">
        <AlertCircle className="h-3.5 w-3.5 mt-px shrink-0" />
        When multiple rules match, higher <strong>Priority</strong> wins. On a tie, the most-specific match wins (engineer + customer + band beats engineer alone).
      </p>
    </section>
  );
};

// ───────────────────── Tester ─────────────────────
const ResolverTester = ({ employees, customers, rateById }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [at, setAt] = useState(() => isoDateTimeLocal(new Date()));
  const [result, setResult] = useState(null);

  const run = async () => {
    try {
      const r = await testRateResolve({
        employeeId,
        customerId,
        at: new Date(at).toISOString(),
      });
      setResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resolve failed');
    }
  };

  return (
    <section data-testid="rate-cards-tester">
      <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <FlaskConical className="h-4 w-4 text-blue-700" /> Tester
      </h2>
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <PickerField label="Engineer" value={employeeId} onChange={setEmployeeId}
          options={[{ id: '', label: 'Any engineer' }, ...employees.map(e => ({ id: e.id, label: e.name }))]}
          testId="tester-employee" />
        <PickerField label="Customer" value={customerId} onChange={setCustomerId}
          options={[{ id: '', label: 'Any customer' }, ...customers.map(c => ({ id: c.id, label: c.company || c.name }))]}
          testId="tester-customer" />
        <div>
          <Label className="text-xs">Date / time</Label>
          <Input type="datetime-local" value={at} onChange={e => setAt(e.target.value)} className="mt-1" data-testid="tester-at" />
        </div>
        <Button onClick={run} className="bg-slate-900 hover:bg-slate-800" data-testid="tester-run">Resolve</Button>
      </div>

      {result && (
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-4" data-testid="tester-result">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Result</div>
          {result.matched ? (
            <div>
              <div className="text-lg font-semibold text-slate-900">£{result.rate.toFixed(2)}/h · {result.rateName}</div>
              <div className="text-xs text-slate-600 mt-1">
                Time band: <span className="font-medium">{result.timeBand}</span>
                {' · '}Matched rule <span className="font-mono">{result.ruleId}</span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              No rule matches this combination. Engineer hours would be logged with <strong>rate £0</strong> and flagged for admin review on the work order.
            </div>
          )}
        </div>
      )}
    </section>
  );
};

// ───────────────────── Bank holidays ─────────────────────
const BankHolidayEditor = () => {
  const [dates, setDates] = useState([]);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getBankHolidays().then(r => setDates(r.dates || [])).catch(() => {});
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await setBankHolidays(next);
      setDates(next);
      toast.success('Bank holidays updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const add = () => {
    if (!draft) return;
    if (dates.includes(draft)) { setDraft(''); return; }
    save([...dates, draft]);
    setDraft('');
  };
  const remove = (d) => save(dates.filter(x => x !== d));

  return (
    <section data-testid="rate-cards-bank-holidays">
      <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-blue-700" /> Bank holidays
      </h2>
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {dates.length === 0 && <div className="text-sm text-slate-500">No bank holidays set.</div>}
          {dates.map(d => (
            <span key={d} className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-900 text-xs px-2 py-1 rounded-full" data-testid={`bh-chip-${d}`}>
              {d}
              <button
                type="button"
                onClick={() => remove(d)}
                disabled={saving}
                className="text-blue-700 hover:text-blue-900"
                aria-label={`Remove ${d}`}
              >×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1 max-w-xs">
            <Label className="text-xs">Add date</Label>
            <Input type="date" value={draft} onChange={e => setDraft(e.target.value)} className="mt-1" data-testid="bh-input" />
          </div>
          <Button size="sm" onClick={add} disabled={!draft || saving} data-testid="bh-add-btn">Add</Button>
        </div>
      </div>
    </section>
  );
};

// ───────────────────── Seed UK standard rates dialog ─────────────────────
// Creates 8 rules at typical UK multipliers from a single base £/h:
//   weekday day 1.0× | weekday night 1.5×
//   Saturday 1.5× (both bands) | Sunday & BH 2.0× (both bands)
// Lets the admin scope the block to a specific engineer and/or customer, or
// leave both as "Any" for a default house rate card.
const STANDARD_MULTIPLIERS = [
  ['Weekday · 06:00–18:00', 1.0],
  ['Weekday · 18:00–06:00', 1.5],
  ['Saturday · 06:00–18:00', 1.5],
  ['Saturday · 18:00–06:00', 2.0],
  ['Sunday · 06:00–18:00', 2.0],
  ['Sunday · 18:00–06:00', 2.5],
  ['Bank holiday · 06:00–18:00', 2.0],
  ['Bank holiday · 18:00–06:00', 2.0],
];

const SeedStandardDialog = ({ open, onClose, employees, customers, onSeeded }) => {
  const [base, setBase] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [priority, setPriority] = useState(0);
  const [busy, setBusy] = useState(false);

  const baseNum = parseFloat(base);
  const preview = Number.isFinite(baseNum) && baseNum > 0 ? baseNum : null;

  const reset = () => {
    setBase(''); setEmployeeId(''); setCustomerId(''); setPriority(0);
  };

  const submit = async () => {
    if (!preview) return toast.error('Enter a base £/hour');
    setBusy(true);
    try {
      const res = await seedStandardRates({
        base: preview,
        employeeId: employeeId || null,
        customerId: customerId || null,
        priority: Number(priority) || 0,
      });
      toast.success(`Created ${res.created} rules`);
      reset();
      onClose();
      await onSeeded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); reset(); } }}>
      <DialogContent className="sm:max-w-lg" data-testid="seed-standard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-700" />
            Seed UK standard rates
          </DialogTitle>
          <DialogDescription>
            Creates 8 rules in one go at UK-typical multipliers of your base hourly rate.
            You can tweak or delete any individual rule afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Base rate (£/hour)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder="e.g. 65.00"
              value={base}
              onChange={e => setBase(e.target.value)}
              className="mt-1"
              data-testid="seed-base-input"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PickerField
              label="Engineer (optional)"
              value={employeeId}
              onChange={setEmployeeId}
              options={[{ id: '', label: 'Any engineer' }, ...employees.map(e => ({ id: e.id, label: e.name }))]}
              testId="seed-employee"
            />
            <PickerField
              label="Customer (optional)"
              value={customerId}
              onChange={setCustomerId}
              options={[{ id: '', label: 'Any customer' }, ...customers.map(c => ({ id: c.id, label: c.company || c.name }))]}
              testId="seed-customer"
            />
          </div>

          <div>
            <Label className="text-xs">Priority</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={priority}
              onChange={e => setPriority(e.target.value)}
              className="mt-1"
              data-testid="seed-priority"
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Preview</div>
            <ul className="text-sm divide-y divide-slate-200">
              {STANDARD_MULTIPLIERS.map(([label, mult]) => (
                <li key={label} className="flex justify-between py-1.5">
                  <span className="text-slate-700">{label}</span>
                  <span className="text-slate-900 font-medium tabular-nums">
                    {preview ? `£${(preview * mult).toFixed(2)}` : '—'}
                    <span className="text-slate-400 font-normal"> · {mult.toFixed(1)}×</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); reset(); }} disabled={busy}>Cancel</Button>
          <Button
            className="bg-slate-900 hover:bg-slate-800"
            onClick={submit}
            disabled={!preview || busy}
            data-testid="seed-submit"
          >
            {busy ? 'Creating…' : 'Create 8 rules'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



// ───────────────────── Helpers ─────────────────────
// Radix Select forbids empty-string SelectItem values. We use a sentinel
// `__any__` token to represent the wildcard ("any engineer / any customer /
// any band") and translate it back to empty string on change.
const ANY = '__any__';
const PickerField = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Select
      value={value || ANY}
      onValueChange={(v) => onChange(v === ANY ? '' : v)}
    >
      <SelectTrigger className="mt-1" data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>
        {options.map(o => (
          <SelectItem key={o.id || ANY} value={o.id || ANY}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export default RateCards;
